// @flow

import * as React from 'react';
import compose from 'lodash/fp/compose';
import connect from '@active/awe-ui-core/lib/hoc/connect';
import injectL10n from 'react-aaui/lib/shared/injectL10n';
import type { L10n, Event, PortalPath } from 'type/type';
import SearchInput from 'react-aaui/lib/SearchInput';
import Row from 'react-aaui/lib/grid/Row';
import Col from 'react-aaui/lib/grid/Col';
import Container from 'components/Container/Container';
import SocialShares from '@active/awe-ui-core/lib/components/SocialShares';
import Maybe, { maybe } from '@active/awe-ui-core/lib/fp/Maybe';
import getSocialShareConfig from 'services/socialShare/socialShare';
import prop from 'lodash/fp/prop';
import eq from 'lodash/fp/eq';
import flow from 'lodash/fp/flow';
import toLower from 'lodash/fp/toLower';
import {
  getFacebookAppId, getStaticContentServiceUrl, getPortalPath, hasUsedPortalPath,
} from 'modules/app/selector';
import appendPortalPath from 'services/router/appendPortalPath';
import {
  EVENT_PARTICIPANTS_PATH,
  EVENT_PATH,
} from 'services/router/enum';
import { getKeyFromQuery } from 'services/query/query';
import { APP_MODE_KIOSK } from 'services/kiosk/kiosk';
import { stringify } from 'query-string';

import routable from 'hoc/router/routable';
import type { Routable } from 'hoc/router/routable';
import { searchParticipant, endSearch } from '../duck/duck';
import './HeaderBar.scss';

type Props = Routable & {
  path: string,
  l10n: L10n,
  onSearch: (keyword: string, applicationName: string) => void,
  applicationName: string,
  keyword: string,
  endSearch: () => void,
  facebookAppId: number,
  event?: ?Event,
  staticContentServiceUrl: string,
  sharingHref: string,
  sharingText: string,
  emailSubject: string,
  participantId: string,
  portalPath: PortalPath,
  hasPortalPath: boolean,
}

type State = {
  timer: number,
}

export class HeaderBar extends React.Component<Props, State> {
  interval: ?IntervalID

  static defaultProps = {
    event: null,
  }

  constructor(props: Props) {
    super(props);

    this.state = { timer: 0 };
  }

  startTimer() {
    const interval = setInterval(() => {
      this.setState(({ timer }: State) => ({
        timer: timer + 1,
      }));
    }, 1000);
    this.interval = interval;
  }

  resetTimer = () => {
    this.setState({ timer: 0 });
  }

  componentDidMount() {
    this.startTimer();
    document.addEventListener('click', this.resetTimer);
    document.addEventListener('keydown', this.resetTimer);
    document.addEventListener('scroll', this.resetTimer);
  }

  componentDidUpdate() {
    const { timer } = this.state;
    const {
      portalPath,
      query,
      isRouterPath,
      hasPortalPath,
    } = this.props;

    if (timer >= 15
      && this.isKioskMode()
      && !isRouterPath(EVENT_PATH, hasPortalPath)
      && !hasPortalPath) {
      window.location.assign(appendPortalPath(
        portalPath,
        `/events/${this.props.applicationName}?${stringify(query)}`,
      ));
    }
  }

  componentWillUnmount() {
    const { interval } = this;
    interval && clearInterval(interval);
    document.removeEventListener('click', this.resetTimer);
    document.removeEventListener('keydown', this.resetTimer);
    document.removeEventListener('scroll', this.resetTimer);
  }

  isKioskMode() {
    const { query } = this.props;

    const appModeKey = getKeyFromQuery()({ key: 'appMode', query });
    return flow(
      prop(appModeKey),
      toLower,
      eq(APP_MODE_KIOSK),
    )(query);
  }

  search = (keyword: string) => {
    if (keyword !== '') {
      this.props.onSearch(keyword, this.props.applicationName);
      this.navToSearch();
    } else {
      this.props.endSearch();
      this.navToEventHome();
    }
  }

  navToSearch() {
    const {
      isRouterPath,
      transitionTo,
      portalPath,
      hasPortalPath,
      query,
    } = this.props;
    const routerPath = EVENT_PARTICIPANTS_PATH;

    if (!isRouterPath(routerPath, hasPortalPath)) {
      transitionTo(appendPortalPath(
        portalPath,
        `/events/${this.props.applicationName}/participants?${stringify(query)}`,
      ));
    }
  }

  navToEventHome() {
    const {
      isRouterPath,
      transitionTo,
      portalPath,
      hasPortalPath,
      query,
    } = this.props;
    const routerPath = EVENT_PARTICIPANTS_PATH;

    if (isRouterPath(routerPath, hasPortalPath)) {
      transitionTo(appendPortalPath(
        portalPath,
        `/events/${this.props.applicationName}?${stringify(query)}`,
      ));
    }
  }

  render() {
    const {
      l10n: { formatMessage },
      keyword,
      event,
      facebookAppId,
      staticContentServiceUrl,
      sharingHref,
      sharingText,
      emailSubject,
      hasPortalPath,
    } = this.props;

    const evt = Maybe.of(event);
    const defaultLogo = `${staticContentServiceUrl}/static/endurance/socialsharing/active_logo.png`;
    const pinterestLogo = maybe(null, prop('logo'), evt) || defaultLogo;

    const socialSharesConfig = getSocialShareConfig(
      facebookAppId,
      sharingHref,
      pinterestLogo,
      sharingText,
      emailSubject);

    return (
      <Container className="event-header-bar">
        <Row fluid justify="between" align="center">
          <Col sm={5}>
            <SearchInput
              size="lg"
              className="event-header-bar__search"
              trigger="enter"
              onSearch={this.search}
              value={keyword}
              placeholder={formatMessage('page.event.participant.search')} />
          </Col>
          {(!this.isKioskMode() || hasPortalPath) && (
            <Col sm={4} md={3} className="event-header-bar__social-share">
              <SocialShares {...socialSharesConfig} />
            </Col>
          )}
        </Row>
      </Container>
    );
  }
}

const mapState = (state) => {
  return {
    keyword: state.getIn([ 'eventPage', 'keyword' ]),
    facebookAppId: getFacebookAppId(state),
    staticContentServiceUrl: getStaticContentServiceUrl(state),
    portalPath: getPortalPath(state),
    hasPortalPath: hasUsedPortalPath(state),
  };
};
const mapDispatch = (dispatch) => {
  return {
    onSearch: (keyword, applicationName) => dispatch(
      searchParticipant(keyword, applicationName, 1)),
    endSearch: () => dispatch(endSearch()),
  };
};

export default compose(
  routable,
  injectL10n(),
  connect(mapState, mapDispatch),
)(HeaderBar);
